import React from 'react';
import { SxStyle } from '../../../../types';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useStringsContext } from '../../../../contexts/strings';

const styles: SxStyle = {
  root: {},
  paper: {
    minWidth: 300,
    height: '250px',
    overflow: 'auto',
  },
  button: (theme) => ({
    margin: theme.spacing(0.5, 0),
  }),
};

function not(a: string[], b: string[]) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a: string[], b: string[]) {
  return a.filter((value) => b.indexOf(value) !== -1);
}

export default function HomeTeamSelector({
  selectedTeams,
  selectableTeams,
  setSelectedTeams,
}: {
  selectedTeams: string[];
  selectableTeams: string[];
  setSelectedTeams: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { strings } = useStringsContext();
  const [checked, setChecked] = React.useState<string[]>([]);
  const [left, setLeft] = React.useState<string[]>(selectableTeams);
  const [right, setRight] = React.useState<string[]>(selectedTeams);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value: string) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    const selectedTeams = right.concat(left);
    setRight(selectedTeams);
    setSelectedTeams(selectedTeams);
    setLeft([]);
  };

  const handleCheckedRight = () => {
    const selectedTeams = right.concat(leftChecked);
    setRight(selectedTeams);
    setSelectedTeams(selectedTeams);
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    const selectedTeams = not(right, rightChecked);
    setLeft(left.concat(rightChecked));
    setRight(selectedTeams);
    setSelectedTeams(selectedTeams);
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    setLeft(left.concat(right));
    setRight([]);
    setSelectedTeams([]);
  };

  const customList = (items: string[]) => (
    <Paper sx={styles.paper}>
      <List dense component="div" role="list">
        {items.map((value: string, index: number) => {
          const labelId = `transfer-list-item-${value}-label${index}`;
          return (
            <ListItem key={labelId} role="listitem" onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value} />
            </ListItem>
          );
        })}
        <ListItem />
      </List>
    </Paper>
  );

  return (
    <>
      <Typography variant="subtitle1">{strings.wifi.homeTeamSelector}</Typography>
      <Grid container spacing={2} alignItems="center" sx={styles.root}>
        <Grid item xs>
          {customList(left)}
        </Grid>
        <Grid item>
          <Grid container direction="column" alignItems="center">
            <Button
              variant="outlined"
              size="small"
              sx={styles.button}
              onClick={handleAllRight}
              disabled={left.length === 0}
              aria-label="move all right"
            >
              ≫
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={styles.button}
              onClick={handleCheckedRight}
              disabled={leftChecked.length === 0}
              aria-label="move selected right"
            >
              &gt;
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={styles.button}
              onClick={handleCheckedLeft}
              disabled={rightChecked.length === 0}
              aria-label="move selected left"
            >
              &lt;
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={styles.button}
              onClick={handleAllLeft}
              disabled={right.length === 0}
              aria-label="move all left"
            >
              ≪
            </Button>
          </Grid>
        </Grid>
        <Grid item xs>
          {customList(right)}
        </Grid>
      </Grid>
    </>
  );
}
